import { compact } from 'lodash';
import { batch } from 'react-redux';
import { logError } from 'api/errors';

// parsers
import {
  parseScript,
  parseScriptListWithFieldId,
  parseScriptsForFertilityActivityUuid,
  parseManyScripts
} from 'scripts/scriptLoadParser';

// finders
import { findScriptProducts } from 'scripts/finders';

// action creators
import {
  addRawScripts,
  storeRawScriptList
} from 'export-selector';
import {
  removeScripts,
  storeScriptList,
  storeScript
} from 'scripts';
import { setResourceLoaded } from 'loading';

import { resourceType } from 'constants/loading';
import { queryClient } from 'common/providers/QueryProvider';
import { getProgramInstanceQueryOptions } from 'manage/hooks/useProgramInstance';

export const processScriptListForFieldId = ({ dispatch, fieldId, rawList }) => {
  // pick out scripts for this field for caching/further fetching
  rawList = rawList.data;
  const scriptsForFieldId = rawList.filter(script => `${script['field-id']}` === fieldId);
  const scripts = parseScriptListWithFieldId(scriptsForFieldId, fieldId);
  dispatch(storeScriptList({scripts}));
  // store raw scripts for export options
  dispatch(storeRawScriptList(rawList));

  return Promise.resolve(scripts);
};

export const processScriptsSummariesForFieldId = async ({ dispatch, fieldId, fieldUuid, rawList }) => {
  const scripts = parseScriptListWithFieldId(rawList, fieldId);
  dispatch(storeScriptList({ scripts }));
  dispatch(addRawScripts(rawList));
  dispatch(setResourceLoaded({ type: resourceType.RAW_SCRIPTS_BY_FIELD_UUID, id: fieldUuid }));
  return scripts;
};

export const processScriptsForActiveOperation = async ({ dispatch, rawList, fieldUuids }) => {
  // TODO: remove batch usage after upgrading to React 18 (https://react-redux.js.org/api/batch)
  batch(() => {
    dispatch(addRawScripts(rawList));
    fieldUuids.forEach((id) => {
      dispatch(setResourceLoaded({ type: resourceType.RAW_SCRIPTS_BY_FIELD_UUID, id }));
    });
  });
  return rawList;
};

export const processScriptListForFertilityActivityUuid = ({ rawScriptList }) => {
  const scripts = parseScriptsForFertilityActivityUuid(rawScriptList);
  if (scripts) {
    return Promise.resolve(scripts);
  }
  return Promise.reject('scripts response was empty');
};

export const processScript = ({ dispatch, fieldId, getState, rawScript }) => {
  if (rawScript['program-instance-id']) {
    queryClient.prefetchQuery(getProgramInstanceQueryOptions(rawScript['program-instance-id']));
  }
  return findScriptProducts({ dispatch, getState, script: rawScript })
  .then((scriptProducts) => {
    try {
      const script = parseScript({ fieldId, getState, script: rawScript, scriptProducts });
      if (script) {
        dispatch(storeScript(script));
        return Promise.resolve(script);
      }
    } catch (e) {
      dispatch(removeScripts([rawScript.id]));
      logError({ error: e, message: `quietly skipping script ${rawScript.id} because of parsing fail`});
      return null; // indicate parsing failure
    }
    return Promise.reject('script response was empty');
  });
};

export const processManyScripts = ({ dispatch, fieldId, getState, rawScripts }) => {
  return Promise.resolve()
  .then( () => {
    const rawScriptList = [...parseManyScripts({ fieldId, getState, rawScripts})];
    const scriptList = [];
    rawScriptList.forEach( (rawScript) => {
      scriptList.push(processScript({ dispatch, fieldId, getState, rawScript}));
    });
    return Promise.all(scriptList);
  })
  // strip out the failures
  .then( (promises) => compact(promises) );
};
